import { FunctionComponent, useCallback, useEffect, useState, useRef, useMemo, type MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { IConnection } from '../../interfaces/models/connection'
import { useExternalHandlers } from '../../services/actions/navigation/useExternalHandlers'
import { useAppStore } from '../../stores/application/useAppStore'
import { AppRoutes, SidebarTypes, TMenuItemState } from '../../interfaces/store/appStore'
import DropdownMenu from '../../components/dropdown/DropdownMenu'
import SvgFragment from '../SvgFragment'
import { useAppHandlers } from '../../services/actions/app/useAppHandlers'
import { useConnectionsHandlers } from '../../services/actions/connections/useConnectionsHandlers'
import Row from '../../components/Row'
import { useAuthStore } from '../../stores/auth/useAuthStore'
import { hasCampaignsAccess } from '../../services/campaigns/campaignsNavAccess'
import { useAuthHandlers } from '../../services/actions/auth/useAuthHandlers'
import styled, { createGlobalStyle } from 'styled-components'
import { styles } from '../../styles/themes/style'
import { isLNFirst } from './../../utils';
import { useExtensionRequest } from '../../services/apis/extension/useExtensionRequest'
import { usePostsHandlers } from '../../services/actions/posts/usePostsHandlers'
import TooltipWrapper from '../../components/TooltipWrapper'
import { useHubspotHandlers } from '../../services/actions/hubspot/useHubspotHandlers'
import { useNavigate } from 'react-router-dom'
import Checkbox from '../../components/Checkbox'
import Text from '../../components/Text'
import { IKanban } from '../../interfaces/models/kanban'
import { ICampaign } from '../../interfaces/models/campaign'
import { ChevronRight, ExternalLink, EyeOff, Kanban, Pencil, RefreshCw, Rss, Share2, Tag, UserMinus, UserPlus, UserX, WandSparkles } from 'lucide-react'

// Shared kanban list cache across all dropdown instances.
const kanbansCacheByWorkspaceId = new Map<string, IKanban[]>();
const kanbansInFlightByWorkspaceId = new Map<string, Promise<any>>();

// Shared draft-campaign list cache per user.
const draftCampaignsCacheByUserId = new Map<string, ICampaign[]>();
const draftCampaignsInFlightByUserId = new Map<string, Promise<ICampaign[]>>();

interface IConnectionActionDropdownProps { 
    connection: IConnection,
    backgroundColor?: string,
    forceShow?: boolean,
    onToggle?: (isOpen: boolean) => void,
    fromPipeline?: boolean,
    onRemoveFromPipeline?: () => void,
    hidePrimaryActions?: boolean,
    onConnectionUpdated?: (connection: IConnection) => void,
    /** When opened from pipeline view, use the board's kanban list (avoids stale/empty local fetch). */
    pipelineKanbans?: IKanban[],
    /** Currently viewed pipeline — ensured to appear in the submenu list. */
    activePipeline?: IKanban,
}

const ConnectionActionDropdown: FunctionComponent<IConnectionActionDropdownProps> = ({connection, backgroundColor, forceShow, onToggle, fromPipeline, onRemoveFromPipeline, hidePrimaryActions, onConnectionUpdated, pipelineKanbans, activePipeline}) => {
    const { openSidebar, closeSidebar, openExtension, setScheduleDisconnectModalHandler, setCalculateEnrichmentModalHandler, setBulkEditTagsModal, setCreateFeedModal, setExportToHubspotModalHandler, showErrorToast, showSuccessToast } = useAppHandlers()
    const { store: { user, isLoggedToLinkedin, workspace, trial } } = useAuthStore()
    const { store: { extensionInstalled, extensionVersion } } = useAppStore()
    const { saveOnboardingData } = useAuthHandlers()
    const { visitChromeStore } = useExternalHandlers()
    const { getProfilePosts } = useExtensionRequest()
    const { updateConnectionHandler, visitConnectionAndUpdateHandler, followProfileHandler, unfollowProfileHandler, toggleHideConnectionHandler, scheduleConnectionsRemovalHandler, addConnectionToKanbanHandler, removeConnectionFromKanbanHandler, calculateEnrichmentHandler, getKanbansHandler, messageClickHandler, getDraftCampaignsHandler, addConnectionsToCampaignHandler } = useConnectionsHandlers()
    const { syncProfilePostsHandler,  } = usePostsHandlers()
    const { exportToHubspotHandler } = useHubspotHandlers()
    const navigate = useNavigate()

    const hubspotEnabled = workspace?.hubspotEnabled === true

    //some of them are for the sidebar and some are for the table, pay attention

    const isExtensionVersionCorrect = extensionVersion === process.env.REACT_APP_VERSION

    const [enableLinkedinAction, setEnableLinkedinAction] = useState<boolean>(false)

    useEffect(() => {
        let isEnabled = false
        
        if (fromPipeline) {
            const isMyConnection = (connection as any)?.user === user?._id
            const hasLinkedIn1stTag = connection?.tags?.some((t: any) => t.title === 'LinkedIn 1st' || t.title === 'New Connection')
            isEnabled = isMyConnection && hasLinkedIn1stTag
        } else {
            isEnabled = isLNFirst(connection, user?._id)
        }
        
        setEnableLinkedinAction(isEnabled)
    }, [connection, fromPipeline, user?._id])

    const updateProfile = useCallback(async () => {
        const result = await updateConnectionHandler(connection)
        if (result) onConnectionUpdated?.(result)
    }, [connection, updateConnectionHandler, onConnectionUpdated])

    const followProfile = useCallback(async () => {
        const result = await followProfileHandler(connection)
        if(result) connection = result //This should be state
    }, [connection])

    const unfollowProfile = useCallback(async () => {
        const result = await unfollowProfileHandler(connection)
        if(result) connection = result //This should be state
    }, [connection])

    const toggleHideUnhide = useCallback(async (value: boolean) => {
        const result = await toggleHideConnectionHandler(connection, value)
        if(result) connection = result //This should be state
    }, [connection])
    
    const installExtensionHandler = useCallback(async () => {
        await Promise.all([
            saveOnboardingData({
                startedExtensionInstallation: true,
            }),
            (async () => visitChromeStore())(),
        ]);
    },[]); 

    // Kanban sub-menu states
    const [showKanbans, setShowKanbans] = useState(false);
    const [kanbansPosition, setKanbansPosition] = useState({ x: 0, y: 0 });
    const [kanbans, setKanbans] = useState<IKanban[]>([]);
    const [isUpdatingKanban, setIsUpdatingKanban] = useState<{ [key: string]: boolean }>({});
    const [optimisticKanbanStates, setOptimisticKanbanStates] = useState<{
      [kanbanId: string]: boolean;
    }>({});
    const kanbanDropdownRef = useRef<HTMLDivElement>(null);
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [forceCloseDropdown, setForceCloseDropdown] = useState(false);

    // Campaign sub-menu states
    const [showCampaigns, setShowCampaigns] = useState(false);
    const [campaignsPosition, setCampaignsPosition] = useState({ x: 0, y: 0 });
    const [draftCampaigns, setDraftCampaigns] = useState<ICampaign[]>([]);
    const [isEnrollingCampaign, setIsEnrollingCampaign] = useState<Record<string, boolean>>({});
    const campaignDropdownRef = useRef<HTMLDivElement>(null);
    const campaignHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const closeMenu = useCallback(() => {
        onToggle?.(false);
        setShowKanbans(false);
        setShowCampaigns(false);
        setForceCloseDropdown(true);
        window.setTimeout(() => setForceCloseDropdown(false), 100);
    }, [onToggle]);

    // Get connection's kanban IDs from tags (tags with forKanban: true have kanban property)
    const connectionKanbanIds = useMemo(
      () => (connection.tags || [])
        .filter((tag: any) => tag.forKanban === true && tag.kanban)
        .map((tag: any) => tag.kanban),
      [connection.tags]
    );

    const getKanbansHandlerRef = useRef(getKanbansHandler);
    useEffect(() => {
      getKanbansHandlerRef.current = getKanbansHandler;
    }, [getKanbansHandler]);

    const loadKanbans = useCallback(async () => {
      const workspaceId = workspace?._id;
      if (!workspaceId) return;

      // If pipeline view provided the list, never fetch here.
      if (pipelineKanbans?.length) return;

      // Shared cache across all instances.
      const cached = kanbansCacheByWorkspaceId.get(workspaceId);
      if (cached?.length) {
        setKanbans(cached);
        return;
      }

      // Reuse shared in-flight promise if it exists.
      const inFlight = kanbansInFlightByWorkspaceId.get(workspaceId);
      if (inFlight) {
        const result = await inFlight;
        if (result?.success && result?.data) {
          kanbansCacheByWorkspaceId.set(workspaceId, result.data);
          setKanbans(result.data);
        }
        return;
      }

      const promise = Promise.resolve(getKanbansHandlerRef.current(workspaceId))
        .finally(() => {
          kanbansInFlightByWorkspaceId.delete(workspaceId);
        });
      kanbansInFlightByWorkspaceId.set(workspaceId, promise);

      const result = await promise;
      if (result?.success && result?.data) {
        kanbansCacheByWorkspaceId.set(workspaceId, result.data);
        setKanbans(result.data);
      }
    }, [workspace?._id, pipelineKanbans?.length]);

    // Intentionally do NOT auto-fetch on mount (many dropdown instances mount at once).
    // We fetch lazily on hover/open, and the request is shared via the cache above.

    const effectiveKanbans = useMemo(() => {
      const base = pipelineKanbans?.length ? pipelineKanbans : kanbans;
      if (activePipeline?._id && !base.some((k) => k._id === activePipeline._id)) {
        return [activePipeline, ...base];
      }
      return base;
    }, [pipelineKanbans, kanbans, activePipeline]);

    const calculateKanbanPosition = useCallback(
      (element: HTMLElement | null) => {
        if (!element) return { x: 0, y: 0 };

        const rect = element.getBoundingClientRect();
        const dropdownWidth = 200;
        const gap = 8;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const dropdownHeight =
          kanbanDropdownRef.current?.offsetHeight ||
          Math.min(effectiveKanbans.length * 32 + 100, 400);

        let x = rect.right + gap;
        let y = rect.top - 6;

        if (x + dropdownWidth > viewportWidth) {
          x = rect.left - dropdownWidth - gap;
        }

        if (y + dropdownHeight > viewportHeight) {
          y = rect.bottom - dropdownHeight + 6;
          if (y < 0) {
            y = viewportHeight - dropdownHeight - 20;
          }
        }

        if (x < 10) x = 10;
        if (y < 10) y = 10;

        return { x, y };
      },
      [effectiveKanbans.length]
    );

    const handleKanbansHover = useCallback(
      (e: React.MouseEvent) => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }

        const element = e.currentTarget as HTMLElement | null;
        hoverTimeoutRef.current = setTimeout(() => {
          void (async () => {
            await loadKanbans();
            const position = calculateKanbanPosition(element);
            setKanbansPosition(position);
            setShowKanbans(true);
          })();
        }, 300);
      },
      [calculateKanbanPosition, loadKanbans]
    );

    const handleKanbansMouseLeave = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setShowKanbans(false);
      }, 200);
    }, []);

    const handleKanbanDropdownMouseEnter = useCallback(() => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    }, []);

    const handleKanbanDropdownMouseLeave = useCallback(() => {
      setShowKanbans(false);
    }, []);

    const handleKanbanToggle = useCallback(
      async (kanbanId: string, isCurrentlyInKanban: boolean) => {
        setIsUpdatingKanban((prev) => ({ ...prev, [kanbanId]: true }));

        setOptimisticKanbanStates((prev) => ({
          ...prev,
          [kanbanId]: !isCurrentlyInKanban,
        }));

        try {
          if (isCurrentlyInKanban) {
            // Remove from kanban
            await removeConnectionFromKanbanHandler(connection._id, { kanbanId });
          } else {
            // Add to kanban
            await addConnectionToKanbanHandler(
              { connectionId: connection._id, kanbanId },
              false
            );
          }
        } catch (error) {
          console.error('Error toggling kanban:', error);
          setOptimisticKanbanStates((prev) => {
            const newState = { ...prev };
            delete newState[kanbanId];
            return newState;
          });
        } finally {
          setIsUpdatingKanban((prev) => ({ ...prev, [kanbanId]: false }));
        }
      },
      [connection._id, addConnectionToKanbanHandler, removeConnectionFromKanbanHandler]
    );

    const loadDraftCampaigns = useCallback(async () => {
      const userId = user?._id ? String(user._id) : '';
      if (!userId) return;

      const cached = draftCampaignsCacheByUserId.get(userId);
      if (cached) {
        setDraftCampaigns(cached);
        return;
      }

      const inFlight = draftCampaignsInFlightByUserId.get(userId);
      if (inFlight) {
        const list = await inFlight;
        setDraftCampaigns(list);
        return;
      }

      const promise = getDraftCampaignsHandler().finally(() => {
        draftCampaignsInFlightByUserId.delete(userId);
      });
      draftCampaignsInFlightByUserId.set(userId, promise);

      const list = await promise;
      draftCampaignsCacheByUserId.set(userId, list);
      setDraftCampaigns(list);
    }, [getDraftCampaignsHandler, user?._id]);

    const calculateCampaignPosition = useCallback(
      (element: HTMLElement | null) => {
        if (!element) return { x: 0, y: 0 };

        const rect = element.getBoundingClientRect();
        const dropdownWidth = 220;
        const gap = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownHeight =
          campaignDropdownRef.current?.offsetHeight ||
          Math.min(draftCampaigns.length * 36 + 60, 360);

        let x = rect.right + gap;
        let y = rect.top - 6;

        if (x + dropdownWidth > viewportWidth) {
          x = rect.left - dropdownWidth - gap;
        }
        if (y + dropdownHeight > viewportHeight) {
          y = Math.max(10, viewportHeight - dropdownHeight - 10);
        }
        if (x < 10) x = 10;
        if (y < 10) y = 10;

        return { x, y };
      },
      [draftCampaigns.length],
    );

    const handleCampaignsHover = useCallback(
      (e: React.MouseEvent) => {
        if (campaignHoverTimeoutRef.current) {
          clearTimeout(campaignHoverTimeoutRef.current);
        }
        const element = e.currentTarget as HTMLElement | null;
        campaignHoverTimeoutRef.current = setTimeout(() => {
          void (async () => {
            await loadDraftCampaigns();
            const position = calculateCampaignPosition(element);
            setCampaignsPosition(position);
            setShowCampaigns(true);
          })();
        }, 300);
      },
      [calculateCampaignPosition, loadDraftCampaigns],
    );

    const handleCampaignsMouseLeave = useCallback(() => {
      if (campaignHoverTimeoutRef.current) {
        clearTimeout(campaignHoverTimeoutRef.current);
      }
      campaignHoverTimeoutRef.current = setTimeout(() => {
        setShowCampaigns(false);
      }, 200);
    }, []);

    const handleCampaignDropdownMouseEnter = useCallback(() => {
      if (campaignHoverTimeoutRef.current) {
        clearTimeout(campaignHoverTimeoutRef.current);
      }
    }, []);

    const handleCampaignDropdownMouseLeave = useCallback(() => {
      setShowCampaigns(false);
    }, []);

    const handleCampaignEnroll = useCallback(
      async (campaignId: string) => {
        setIsEnrollingCampaign(prev => ({ ...prev, [campaignId]: true }));
        try {
          closeMenu();
          setShowCampaigns(false);
          await addConnectionsToCampaignHandler(campaignId, [connection._id]);
        } finally {
          setIsEnrollingCampaign(prev => ({ ...prev, [campaignId]: false }));
        }
      },
      [addConnectionsToCampaignHandler, closeMenu, connection._id],
    );

    // Close kanbans dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = () => {
        if (showKanbans) setShowKanbans(false);
        if (showCampaigns) setShowCampaigns(false);
      };

      if (showKanbans || showCampaigns) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showKanbans, showCampaigns]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (campaignHoverTimeoutRef.current) clearTimeout(campaignHoverTimeoutRef.current);
      };
    }, []);

    // Clear optimistic state when connection.tags is updated
    useEffect(() => {
      const newConnectionKanbanIds = (connection.tags || [])
        .filter((tag: any) => tag.forKanban === true && tag.kanban)
        .map((tag: any) => tag.kanban);

      setOptimisticKanbanStates((prev) => {
        const newState = { ...prev };
        let hasChanges = false;

        Object.keys(prev).forEach((kanbanId) => {
          const optimisticValue = prev[kanbanId];
          const realValue = newConnectionKanbanIds.includes(kanbanId);

          if (optimisticValue === realValue) {
            delete newState[kanbanId];
            hasChanges = true;
          }
        });

        return hasChanges ? newState : prev;
      });
    }, [connection.tags]);

    // Recalculate position when dropdown becomes visible
    useEffect(() => {
      if (showKanbans && kanbanDropdownRef.current) {
        setTimeout(() => {
          if (kanbanDropdownRef.current) {
            const currentRect = kanbanDropdownRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let { x, y } = kanbansPosition;
            const needsRepositioning =
              x + currentRect.width > viewportWidth ||
              y + currentRect.height > viewportHeight;

            if (needsRepositioning) {
              const gap = 8;
              const triggerX = x - gap;
              const triggerY = y + 6;
              const triggerWidth = 20;

              let newX = triggerX + triggerWidth + gap;
              let newY = triggerY - 6;

              if (newX + currentRect.width > viewportWidth) {
                newX = triggerX - currentRect.width - gap;
              }

              if (newY + currentRect.height > viewportHeight) {
                newY = triggerY - currentRect.height + 6;
                if (newY < 0) {
                  newY = viewportHeight - currentRect.height - 20;
                }
              }

              if (newX < 10) newX = 10;
              if (newY < 10) newY = 10;

              setKanbansPosition({ x: newX, y: newY });
            }
          }
        }, 10);
      }
    }, [showKanbans, kanbansPosition]);

    const enrichConnection = useCallback(async () => {
        setCalculateEnrichmentModalHandler(calculateEnrichmentHandler, () => {}, false, [connection]);
    }, [connection]);

    const updateConectionPosts = useCallback(async () => {
        if (connection && connection.profileId && connection.publicIdentifier && connection.isPostTracked) {
            try {
                // Construct the profile URN
                const profileUrn = `urn:li:fsd_profile:${connection.profileId}`;
                
                const profilePosts = await getProfilePosts(profileUrn);
                
                if (profilePosts) {
                    // Transform and sync posts to backend
                    await syncProfilePostsHandler(profilePosts, connection.publicIdentifier);
                    console.log("Posts successfully synced to backend for:", connection.publicIdentifier);
                } else {
                    console.log("SKIPPING transformProfilePosts because profilePosts is:", profilePosts);
                }
            } catch (error) {
                console.error("Error enabling post tracking:", error);
            }
        }
    }, [connection]);

    const exportToHubspot = useCallback(async (
        includeTags: boolean,
        includeNotes: boolean,
        selectAll: boolean,
        useDefaultMapping: boolean,
        exportType: 'contacts' | 'list' | 'workflow' | 'deals',
        exportDetails?: {
            listId?: string;
            workflowId?: string;
            dealStage?: string;
            dealName?: string;
            amount?: number;
            pipelineId?: string;
        }
    ) => {
        try {
            // Base payload - wrap single connection in array
            const basePayload: any = {
                connectionIds: [connection._id],
                exportAll: false,
                includeTags,
                includeNotes,
                useDefaultMapping,
                exportType,
                // Spread export details to include all type-specific fields
                ...exportDetails
            };

            // Handle field mapping flow
            if (useDefaultMapping) {
                // Use existing mappings and import directly
                const result = await exportToHubspotHandler(basePayload);

                if (result?.success) {
                    showSuccessToast({ message: result.message || 'Successfully exported to HubSpot' });
                }
                else {
                    showErrorToast({ message: 'Error exporting contact to HubSpot' });
                }
            } else {
                // Navigate to mapping page with export parameters for all types
                navigate(AppRoutes.HUBSPOT_MAPPING, {
                    state: {
                        exportParams: basePayload
                    }
                });
            }

        } catch (error) {
            console.error('Export to HubSpot error:', error);
            showErrorToast({ message: 'Error exporting contact to HubSpot' });
        }
    }, [connection, exportToHubspotHandler, navigate, showSuccessToast, showErrorToast]);


    const connectionActionsInitial = [
        {
            icon: 'profile',
            title: fromPipeline ? 'Connect' : 'Visit profile',
            slug: 'visitProfile',
            enabled: !!connection.publicIdentifier,
            tooltip: !connection.publicIdentifier ? 'LinkedIn public identifier is missing' : undefined,
            visible: (!hidePrimaryActions) && (!fromPipeline || !enableLinkedinAction),
            onClickHandler: (connection: IConnection) => visitConnectionAndUpdateHandler(connection)
        }, 
        {
            icon: 'message',
            title: 'Message',
            slug: 'messageProfile',
            enabled: !workspace?.isStarter && isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.publicIdentifier && enableLinkedinAction,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: !workspace?.isStarter && extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && (!connection.publicIdentifier || !enableLinkedinAction) ? 'Contact must be LinkedIn 1st' : undefined,
            visible: (!hidePrimaryActions) && (!fromPipeline || enableLinkedinAction),
            onClickHandler: (connection: IConnection, e?: any) => {
                if (fromPipeline) {
                    // Delay to allow dropdown to close first (messageClickHandler calls stopPropagation)
                    setTimeout(() => messageClickHandler(e, connection), 0)
                } else {
                    openExtension({ query: `redirectTo=inbox&messagedConnection=${connection._id}` })
                }
            }
        },
        {
            icon: 'label',
            title: 'Apply/Remove tags',
            slug: 'applyRemove',
            enabled: true,
            visible: true,
            onClickHandler: (connection: IConnection) => setBulkEditTagsModal([connection._id], false)
        },
        {
            icon: 'pen',
            title: 'Add task',
            slug: 'addTask',
            enabled: true,
            visible: true,
            onClickHandler: (connection: IConnection) => openSidebar(SidebarTypes.TASK_EDIT_CREATE, { formType: 'linked connection', connection })
        },
        {
            icon: 'note',
            title: 'Add note',
            slug: 'addNote',
            enabled: true,
            visible: !!fromPipeline,
            onClickHandler: (connection: IConnection) => openSidebar(SidebarTypes.NOTE_EDIT_CREATE, { 
                connectionId: connection._id, 
                existingNotes: connection.notes,
                onCloseHandler: () => closeSidebar(SidebarTypes.NOTE_EDIT_CREATE)
            })
        },
        {
            icon: 'refresh',
            title: 'Update Profile',
            slug: 'updateProfile',
            enabled: isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.profileId,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && !connection.profileId ? 'Contact must be LinkedIn 1st' : undefined,
            visible: !fromPipeline,
            onClickHandler: (connection: IConnection) => connection.profileId && updateProfile()
        },
        {
            icon: 'profileAdd',
            title: 'Follow',
            slug: 'follow',
            enabled: isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.publicIdentifier && enableLinkedinAction,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && (!connection.publicIdentifier || !enableLinkedinAction) ? 'Contact must be LinkedIn 1st' : undefined,
            visible: !fromPipeline,
            onClickHandler: (connection: IConnection) => connection.profileId && followProfile()
        },
        {
            icon: 'kanban',
            title: 'Add to pipeline',
            slug: 'addToKanban',
            enabled: !workspace?.isStarter,
            tooltip: workspace?.isStarter ? 'Upgrade your plan to unlock this feature' : undefined,
            visible: !fromPipeline,
            onClickHandler: () => {}, // Handled by hover
            hasSubMenu: true,
            onMouseEnter: handleKanbansHover,
            onMouseLeave: handleKanbansMouseLeave,
        },
        {
            icon: 'sideCampaignsLucide',
            title: 'Add to campaign',
            slug: 'addToCampaign',
            enabled: true,
            visible: !fromPipeline && hasCampaignsAccess(workspace, trial),
            onClickHandler: () => {}, // Handled by hover
            hasSubMenu: true,
            onMouseEnter: handleCampaignsHover,
            onMouseLeave: handleCampaignsMouseLeave,
        },
        {
            icon: 'enrich',
            title: 'Enrich',
            slug: 'enrichConnection',
            enabled: true,
            visible: !fromPipeline,
            onClickHandler: (connection: IConnection) => connection && enrichConnection()
        },
        {
            icon: 'feed',
            // title: connection.isPostTracked ? 'Update feed' : 'Add to feed',
            title: 'Add to feed',
            slug: 'addToFeed',
            enabled: isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.profileId && connection.publicIdentifier,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && (!connection.profileId || !connection.publicIdentifier) ? 'Contact must be LinkedIn 1st' : undefined,
            visible: true,
            // onClickHandler: (connection: IConnection) => connection && connection.isPostTracked ? updateConectionPosts() : setCreateFeedModal([connection._id], false)
            onClickHandler: (connection: IConnection) => connection && setCreateFeedModal([connection._id])
        },
        {
            icon: 'exportHubspot',
            title: 'Export to HubSpot',
            slug: 'exportHubspot',
            enabled: hubspotEnabled,
            visible: !fromPipeline,
            showInstallExt: workspace?.isStarter,
            onClickHandler: (connection: IConnection) => {
                setExportToHubspotModalHandler(
                    exportToHubspot,
                    1,
                    false
                )
            }
        },
        {
            icon: 'profileRemove',
            title: 'Unfollow',
            slug: 'unfollow',
            enabled: isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.publicIdentifier && enableLinkedinAction,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && (!connection.publicIdentifier || !enableLinkedinAction) ? 'Contact must be LinkedIn 1st' : undefined,
            visible: !fromPipeline,
            destructive: true,
            onClickHandler: (connection: IConnection) => connection.profileId && unfollowProfile()
        },
        {
            icon: 'hide',
            title: 'Hide',
            slug: 'hide',
            enabled: true,
            visible: !fromPipeline && !connection.isHidden,
            destructive: true,
            onClickHandler: (connection: IConnection) => connection && toggleHideUnhide(true)
        },
        {
            icon: 'show',
            title: 'Unhide',
            slug: 'unhide',
            enabled: true,
            visible: !fromPipeline && (connection.isHidden ?? false),
            onClickHandler: (connection: IConnection) => connection && toggleHideUnhide(false)
        },
        {
            icon: 'profileRemove',
            title: 'Remove',
            slug: 'remove',
            enabled: isLoggedToLinkedin && extensionInstalled && isExtensionVersionCorrect && connection.publicIdentifier && enableLinkedinAction,
            showInstallExt: !extensionInstalled || !isLoggedToLinkedin || !isExtensionVersionCorrect,
            tooltip: extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect && (!connection.publicIdentifier || !enableLinkedinAction) ? 'Contact must be LinkedIn 1st' : undefined,
            visible: !fromPipeline,
            destructive: true,
            //onClickHandler: (connection: IConnection) => setDisconnectConnectionsModalHandler(disconnectRemove, undefined, undefined, undefined, enableLinkedinAction)
            onClickHandler: (connection: IConnection) => setScheduleDisconnectModalHandler(
                () => scheduleDisconnectHandler(connection),
                undefined,
                1
            )
        },
        {
            icon: 'close',
            title: 'Remove',
            slug: 'removeFromPipeline',
            enabled: !!onRemoveFromPipeline,
            visible: !!fromPipeline,
            destructive: true,
            onClickHandler: () => onRemoveFromPipeline && onRemoveFromPipeline()
        }
    ]

    useEffect(() => {
        setConnectionActions(connectionActionsInitial)
    }, [connection, enableLinkedinAction, extensionInstalled, isLoggedToLinkedin, hubspotEnabled, workspace?.isStarter, fromPipeline, hidePrimaryActions])

    const scheduleDisconnectHandler = useCallback(async (connection: IConnection) => {
        if(connection){
          scheduleConnectionsRemovalHandler([connection], false);
        }
    }, []);

    const [ connectionActions, setConnectionActions ] = useState<TMenuItemState[]>(connectionActionsInitial);
    //todo: further adjustment needed, on click handling states depending on the imported state etc.

    const getAction = (slug: string) => connectionActions.find(a => (a as any).slug === slug)

    const dataActions = [
        ...([getAction('visitProfile')].filter(a => a && (a as any).visible) as TMenuItemState[]),
        getAction('updateProfile'),
        getAction('enrichConnection'),
        getAction('exportHubspot'),
    ].filter(Boolean) as TMenuItemState[]
    const organizeActions = [getAction('applyRemove'), getAction('addToKanban'), getAction('addToCampaign'), getAction('addToFeed')].filter(Boolean) as TMenuItemState[]
    const engageActions = [getAction('addTask'), getAction('follow')].filter(Boolean) as TMenuItemState[]
    const manageActions = [getAction('unfollow'), getAction('hide'), getAction('remove')].filter(Boolean) as TMenuItemState[]

    const runMenuAction = useCallback((action: TMenuItemState, e: ReactMouseEvent) => {
        if (!action.enabled || !action.onClickHandler) return;
        closeMenu();
        void Promise.resolve((action.onClickHandler as any)(connection, e));
    }, [connection, closeMenu]);

    const renderRow = (action: TMenuItemState, opts: { icon: React.ReactNode; danger?: boolean; isSubMenu?: boolean }) => {
        const row = (
            <ActionRow $danger={opts.danger} $disabled={!action.enabled}>
                <IconWrap $danger={opts.danger}>{opts.icon}</IconWrap>
                <span>{action.title}</span>
                {opts.isSubMenu && (
                    <ChevronWrap>
                        <ChevronRight size={16} />
                    </ChevronWrap>
                )}
            </ActionRow>
        )

        if ((action as any).tooltip) {
            return (
                <TooltipWrapper tooltipText={(action as any).tooltip}>
                    <div>{row}</div>
                </TooltipWrapper>
            )
        }

        return row
    }

    return <>
        <SidebarActionsDropdownGlobalStyles />
        <DropdownMenu className='connections-actions-dropdown sidebar-actions-v2' SVGtype='threeDots' $hideToggle tooltipMessage={''} $backgroundColor={backgroundColor} forceShow={forceShow} forceClose={forceCloseDropdown} onToggle={onToggle} align="end">
            <MenuPanel>
            {dataActions.length > 0 && (
                <MenuSection>
                    <MenuHeader>Data</MenuHeader>
                    <MenuItems>
                        {dataActions.map(action => {
                            const slug = String((action as any).slug ?? action.title)
                            const icon =
                                slug === 'visitProfile' ? <ExternalLink size={14} /> :
                                slug === 'updateProfile' ? <RefreshCw size={14} /> :
                                slug === 'enrichConnection' ? <WandSparkles size={14} /> :
                                <Share2 size={14} />
                            return (
                                <MenuItem
                                    key={slug}
                                    $disabled={!action.enabled}
                                    onClick={(e) => runMenuAction(action, e)}
                                >
                                    {renderRow(action, { icon })}
                                </MenuItem>
                            )
                        })}
                    </MenuItems>
                </MenuSection>
            )}

            <SectionDivider />

            {organizeActions.length > 0 && (
                <MenuSection>
                    <MenuHeader>Organize</MenuHeader>
                    <MenuItems>
                        {organizeActions.map(action => {
                            const slug = String((action as any).slug ?? action.title)
                            if ((action as any).hasSubMenu) {
                                const icon = slug === 'addToKanban'
                                  ? <Kanban size={14} />
                                  : <SvgFragment type="sideCampaignsLucide" />;
                                return (
                                    <MenuItem
                                        key={slug}
                                        $disabled={!action.enabled}
                                        onMouseEnter={(action as any).onMouseEnter}
                                        onMouseLeave={(action as any).onMouseLeave}
                                    >
                                        {renderRow(action, { icon, isSubMenu: true })}
                                    </MenuItem>
                                )
                            }

                            const icon =
                                slug === 'applyRemove' ? <Tag size={14} /> :
                                <Rss size={14} />

                            return (
                                <MenuItem
                                    key={slug}
                                    $disabled={!action.enabled}
                                    onClick={(e) => runMenuAction(action, e)}
                                >
                                    {renderRow(action, { icon })}
                                </MenuItem>
                            )
                        })}
                    </MenuItems>
                </MenuSection>
            )}

            <SectionDivider />

            {engageActions.length > 0 && (
                <MenuSection>
                    <MenuHeader>Engage</MenuHeader>
                    <MenuItems>
                        {engageActions.map(action => {
                            const slug = String((action as any).slug ?? action.title)
                            const icon = slug === 'addTask' ? <Pencil size={14} /> : <UserPlus size={14} />
                            return (
                                <MenuItem
                                    key={slug}
                                    $disabled={!action.enabled}
                                    onClick={(e) => runMenuAction(action, e)}
                                >
                                    {renderRow(action, { icon })}
                                </MenuItem>
                            )
                        })}
                    </MenuItems>
                </MenuSection>
            )}

            <SectionDivider />

            {manageActions.length > 0 && (
                <MenuSection>
                    <MenuHeader>Manage</MenuHeader>
                    <MenuItems>
                        {manageActions.map(action => {
                            const slug = String((action as any).slug ?? action.title)
                            const danger = true
                            const icon =
                                slug === 'unfollow' ? <UserMinus size={14} /> :
                                slug === 'hide' ? <EyeOff size={14} /> :
                                <UserX size={14} />
                            return (
                                <MenuItem
                                    key={slug}
                                    $disabled={!action.enabled}
                                    onClick={(e) => runMenuAction(action, e)}
                                >
                                    {renderRow(action, { icon, danger })}
                                </MenuItem>
                            )
                        })}
                    </MenuItems>
                </MenuSection>
            )}
        </MenuPanel>
        {showKanbans &&
          createPortal(
            <KanbanDropdownPortal
              ref={kanbanDropdownRef}
              style={{
                position: 'fixed',
                top: kanbansPosition.y,
                left: kanbansPosition.x,
                zIndex: 10000010,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseEnter={handleKanbanDropdownMouseEnter}
              onMouseLeave={handleKanbanDropdownMouseLeave}
            >
              <div
                style={{
                  minWidth: '200px',
                  padding: '16px 0',
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ padding: '0 12px 8px 12px' }}>
                  <Text $paragraph2>Select the pipeline</Text>
                </div>

                {effectiveKanbans.map((kanban: IKanban) => {
                  const originalIsInKanban = connectionKanbanIds.includes(kanban._id);
                  const isInKanban = optimisticKanbanStates.hasOwnProperty(kanban._id)
                    ? optimisticKanbanStates[kanban._id]
                    : originalIsInKanban;
                  const isLoading = isUpdatingKanban[kanban._id];

                  return (
                    <KanbanItem
                      key={kanban._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLoading) {
                          closeMenu();
                          handleKanbanToggle(kanban._id, isInKanban);
                        }
                      }}
                      style={{ opacity: isLoading ? 0.6 : 1 }}
                    >
                      <Row alignItems gap="8px">
                        <Checkbox
                          checked={isInKanban}
                          onClickHandler={(e) => {
                            e.stopPropagation();
                            if (!isLoading) {
                              closeMenu();
                              handleKanbanToggle(kanban._id, isInKanban);
                            }
                          }}
                          checkboxId={`kanban-${kanban._id}-${connection._id}`}
                          disabled={isLoading}
                          $height="24px"
                        />
                        <div style={{ flex: 1 }}>
                          <Text>{kanban.name}</Text>
                        </div>
                        {isLoading && (
                          <div style={{ width: '16px', height: '16px' }}>
                            <SvgFragment type="spinner" />
                          </div>
                        )}
                      </Row>
                    </KanbanItem>
                  );
                })}
              </div>
            </KanbanDropdownPortal>,
            document.body
          )}
        {showCampaigns &&
          createPortal(
            <KanbanDropdownPortal
              ref={campaignDropdownRef}
              style={{
                position: 'fixed',
                top: campaignsPosition.y,
                left: campaignsPosition.x,
                zIndex: 10000010,
              }}
              onMouseDown={e => e.stopPropagation()}
              onMouseEnter={handleCampaignDropdownMouseEnter}
              onMouseLeave={handleCampaignDropdownMouseLeave}
            >
              <div
                style={{
                  minWidth: '220px',
                  padding: '16px 0',
                  background: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <div style={{ padding: '0 12px 8px 12px' }}>
                  <Text $paragraph2>Select campaign</Text>
                </div>
                {draftCampaigns.length > 0 ? (
                  draftCampaigns.map(campaign => {
                    const isLoading = isEnrollingCampaign[campaign._id];
                    return (
                      <KanbanItem
                        key={campaign._id}
                        onClick={e => {
                          e.stopPropagation();
                          if (!isLoading) void handleCampaignEnroll(campaign._id);
                        }}
                        style={{ opacity: isLoading ? 0.6 : 1 }}
                      >
                        <Row alignItems gap="8px">
                          <div style={{ flex: 1 }}>
                            <Text>{campaign.name}</Text>
                          </div>
                          {isLoading && (
                            <div style={{ width: '16px', height: '16px' }}>
                              <SvgFragment type="spinner" />
                            </div>
                          )}
                        </Row>
                      </KanbanItem>
                    );
                  })
                ) : (
                  <div style={{ padding: '8px 12px' }}>
                    <Text $paragraph3 $faded>No campaigns yet</Text>
                  </div>
                )}
              </div>
            </KanbanDropdownPortal>,
            document.body,
          )}
        </DropdownMenu>
    </>
}

const SidebarActionsDropdownGlobalStyles = createGlobalStyle`
  /* Match Figma dropdown for Sidebar actions */
  .sidebar-actions-v2 .actions-dropdown + div,
  .sidebar-actions-v2 .dropdown-menu {
    padding: 0;
    border: 1px solid #e8eaed;
    border-radius: 10px;
    box-shadow: 0px 0px 6px rgba(51, 57, 81, 0.1);
    background: #fff;
    min-width: 238px;
    width: max-content;
    max-height: none !important;
    overflow: hidden !important;
    overflow-y: hidden !important;
    scrollbar-width: none;
  }

  .sidebar-actions-v2 .actions-dropdown + div::-webkit-scrollbar,
  .sidebar-actions-v2 .dropdown-menu::-webkit-scrollbar {
    display: none;
  }

  /* drop="start" adds a bootstrap caret via ::before — hide all carets */
  .sidebar-actions-v2 .dropdown-toggle::after,
  .sidebar-actions-v2 .dropdown-toggle::before,
  .sidebar-actions-v2.dropstart .dropdown-toggle::after,
  .sidebar-actions-v2.dropstart .dropdown-toggle::before,
  .sidebar-actions-v2 .dropdown-toggle.actions-dropdown::after,
  .sidebar-actions-v2 .dropdown-toggle.actions-dropdown::before,
  .sidebar-actions-v2.dropstart .dropdown-toggle.actions-dropdown::after,
  .sidebar-actions-v2.dropstart .dropdown-toggle.actions-dropdown::before {
    display: none !important;
    content: none !important;
    border: 0 !important;
  }
`

const MenuPanel = styled.div`
  padding: 13px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Nunito Sans', sans-serif;
`

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MenuHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #545e77;
`

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const MenuItem = styled.div<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  user-select: none;
  padding: 6px 6px;
  margin: 0;
  border-radius: 8px;
  box-sizing: border-box;
  transition: background-color 0.15s ease;

  ${({ $disabled }) => !$disabled && `
    &:hover {
      background-color: #f3f4f6;
    }
  `}
`

const ActionRow = styled.div<{ $danger?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ $danger }) => ($danger ? '#d14644' : '#41495e')};

  span { white-space: nowrap; }
`

const IconWrap = styled.div<{ $danger?: boolean }>`
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { width: 14px; height: 14px; color: ${({ $danger }) => ($danger ? '#d14644' : styles.colors.primary600)}; }
  .path { stroke: ${({ $danger }) => ($danger ? '#d14644' : styles.colors.primary600)}; }
`

const ChevronWrap = styled.div`
  margin-left: auto;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { color: #545e77; }
`

const SectionDivider = styled.div`
  height: 1px;
  width: 100%;
  background: #dae0e8;
`

const KanbanDropdownPortal = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const KanbanItem = styled.div`
  padding: 6px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f3f4f6;
  }
`;

export default ConnectionActionDropdown
